/**
 * Unified Input Atom
 * @layer atom
 * @package @plerity/ui-core
 */

import { forwardRef, useId, useState } from "react";
import { cn } from "../utils/cn";
import type { InputProps } from "./Input.types";
import { inputVariants } from "./Input.variants";

// Re-exported so `import { inputVariants } from "@plerity/ui-core"` (and the
// component barrel `./Input`) keep working after the leaf-module extraction.
export { inputVariants };

/** Outline "eye" icon — shown while the password is masked (action: reveal). */
function EyeIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

/** "Eye-off" icon — shown while the password is visible (action: hide). */
function EyeOffIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-10-7-10-7a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
      <path d="M1 1l22 22" />
    </svg>
  );
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = "text",
      variant,
      size,
      label,
      error,
      helperText,
      leftIcon,
      rightIcon,
      revealToggle,
      className,
      id,
      required,
      disabled,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const effectiveVariant = error ? "error" : variant;
    const displayHelperText = helperText;

    // Password reveal ("eye") toggle — auto-enabled for password fields so
    // every login/password input across the fleet gets the commercial-standard
    // show/hide affordance for free. Opt out with `revealToggle={false}`.
    const isPassword = type === "password";
    const showReveal = isPassword && revealToggle !== false;
    const [revealed, setRevealed] = useState(false);
    const effectiveType = showReveal && revealed ? "text" : type;
    // The reveal button owns the right slot for password fields.
    const hasRightAffordance = showReveal || Boolean(rightIcon);
    const revealLabel = revealed ? "Hide password" : "Show password";

    return (
      <div className="space-y-1">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-gray-900 dark:text-gray-100">
            {label}
            {required && (
              <span className="ml-0.5 text-danger-strong" aria-hidden="true">
                *
              </span>
            )}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <span
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              aria-hidden="true"
            >
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            type={effectiveType}
            required={required}
            disabled={disabled}
            aria-required={required || undefined}
            aria-invalid={!!error}
            aria-describedby={error ? `${inputId}-error` : displayHelperText ? `${inputId}-helper` : undefined}
            className={cn(
              inputVariants({ variant: effectiveVariant, size }),
              leftIcon && "pl-10",
              hasRightAffordance && "pr-10",
              className
            )}
            {...props}
          />
          {showReveal ? (
            <button
              type="button"
              onClick={() => setRevealed((v) => !v)}
              disabled={disabled}
              aria-label={revealLabel}
              aria-pressed={revealed}
              aria-controls={inputId}
              title={revealLabel}
              tabIndex={disabled ? -1 : 0}
              className="absolute right-1.5 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded text-gray-400 transition-colors hover:text-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-focus disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-500 dark:hover:text-gray-300"
            >
              {revealed ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          ) : (
            rightIcon && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" aria-hidden="true">
                {rightIcon}
              </span>
            )
          )}
        </div>
        {error && (
          <p id={`${inputId}-error`} className="text-sm text-danger-strong" role="alert">
            {error}
          </p>
        )}
        {displayHelperText && !error && (
          <p id={`${inputId}-helper`} className="text-sm text-gray-500 dark:text-gray-400">
            {displayHelperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";
